import {
	ChangeDetectionStrategy,
	Component,
	computed,
	inject,
	input,
	ViewEncapsulation,
} from '@angular/core';
import { RouterModule } from '@angular/router';

import { TranslatePipe } from '@ngx-translate/core';

import { AlertComponent } from '@valk-nx/components/ui-alert/src/lib/alert.component';
import type { Icon } from '@valk-nx/components/ui-icon-list/src/lib/icon-list.interface';
import { PersuadeCardComponent } from '@valk-nx/compositions/ui-persuade-card/src/lib/persuade-card.component';
import { PRICING_TOOLTIP_OCCUPANCY } from '@valk-nx/core/lib/core';
import { BOOKINGTOOL } from '@valk-nx/storyblok-services/src/lib/globals';
import { FilterableListService } from '@valk-nx/storyblok-services/src/lib/services/filterable-list.service';
import type { ContentData } from '@valk-nx/storyblok-types/src/lib/types/content.interface';
import type {
	FilterableHotel,
	TransformedFilterableHotel,
} from '@valk-nx/storyblok-types/src/lib/types/filterable-list.service.interface';

import {
	sortByOption,
	toBaseUrl,
	toPageUrl,
} from '../../filterable-list.helpers';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	host: { class: 'contents' },
	selector: 'sb-filterable-list-vdcvhi-hotels',
	templateUrl: './vdcvhi-hotels.html',
	imports: [
		AlertComponent,
		PersuadeCardComponent,
		RouterModule,
		TranslatePipe,
	],
})
export class VdcvhiHotels {
	private readonly filterableListService = inject(FilterableListService);
	readonly bookingtoolUrl = inject(BOOKINGTOOL);

	sortOption = input.required<string>();
	baseUrl = input.required({ transform: toBaseUrl });
	showPrices = input<boolean>(true);

	readonly pricingTooltipOccupancy = PRICING_TOOLTIP_OCCUPANCY;

	hotels = computed<TransformedFilterableHotel[]>(
		() =>
			this.filterableListService.vdcvhiHotels
				.value()
				?.hotels.map((hotel) => ({
					...hotel,
					content: this.transformContent(hotel.content),
					iconList: this.buildIconList(hotel),
					cardUrl: toPageUrl(this.baseUrl(), hotel.slug),
				})) ?? [],
	);

	sortedHotels = computed<TransformedFilterableHotel[]>(() =>
		sortByOption(this.hotels(), this.sortOption()),
	);

	private readonly transformContent = (content: ContentData): string => {
		if (content?.type === 'array') {
			return content.value.map((item) => `- ${item}`).join('\n');
		}
		return content.value as string;
	};

	private readonly buildIconList = (hotel: FilterableHotel) => {
		const iconList: Icon[] = [];

		if (hotel.totalHotelRooms) {
			iconList.push({
				iconName: 'icon-bed-double',
				title: hotel.totalHotelRooms.toString(),
			});
		}
		if (hotel.totalMeetingRooms) {
			iconList.push({
				iconName: 'icon-suitcase',
				title: hotel.totalMeetingRooms.toString(),
			});
		}
		if (hotel.totalRestaurants) {
			iconList.push({
				iconName: 'icon-fork-knife',
				title: hotel.totalRestaurants.toString(),
			});
		}

		return iconList;
	};
}
